import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef, forwardRef,
  HostBinding,
  HostListener,
  inject, signal,
} from '@angular/core';
import { ComponentHelperService, OverlayService } from '@core/services';
import { IconComponent, SquircleComponent } from '@shared/components';
import { ThrottleClickDirective } from '@shared/directives';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { I18NextModule } from 'angular-i18next';
import { AnalyticsEventsEnum } from '@core/models/analytics.model';
import { AnalyticsDirective } from '@shared/directives/analytics.directive';
import { BreakpointFacade } from '@core/facades';

interface NavBarItem {
  key: string;
  link?: string | string[],
  secondLink?: string | string[],
  action?: () => void;
  icon: {
    defaultName: string;
    activeName: string;
  };
  eventName: AnalyticsEventsEnum;
}

@Component({
  standalone: true,
  selector: 'cheelee-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrl: './nav-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    RouterLinkActive,
    I18NextModule,
    AnalyticsDirective,
    ThrottleClickDirective,
    IconComponent,
    forwardRef(() => SquircleComponent),
  ],
})
export class NavBarComponent implements AfterViewInit {
  @HostListener('window:resize')
  windowResizeListener(): void {
    this.resizeHandler();
  }

  @HostBinding('class')
  get classes(): Record<string, boolean> {
    return {
      'chl-nav-bar--desktop': this.isDesktop(),
    };
  }

  @HostBinding('style')
  get styles(): Record<string, string> {
    return {
      '--chl-nav-bar-header-height': `${this.headerHeight()}px`,
    };
  }

  private readonly breakpointFacade = inject(BreakpointFacade);

  private readonly componentHelperService = inject(ComponentHelperService);

  private readonly navBarWidth = this.componentHelperService.navBarWidth;

  private readonly navBarHeight = this.componentHelperService.navBarHeight;

  private readonly headerHeight = this.componentHelperService.headerHeight;

  private readonly overlayService = inject(OverlayService);

  private readonly elRef = inject(ElementRef);

  protected readonly isDesktop = this.breakpointFacade.isDesktop;

  protected readonly isMobile = this.breakpointFacade.isMobile;

  protected readonly navBarItems: NavBarItem[] = [
    {
      key: 'watch',
      link: '/',
      icon: {
        defaultName: 'tabbar/fire-watch.svg',
        activeName: 'tabbar/fire-watch-filled.svg',
      },
      eventName: AnalyticsEventsEnum.FeedTap,
    },
    {
      key: 'create',
      icon: {
        defaultName: 'tabbar/create.svg',
        activeName: 'tabbar/create.svg',
      },
      action: () => this.overlayService.openGetAppDialog(AnalyticsEventsEnum.CreateTap, 'lee'),
      eventName: AnalyticsEventsEnum.CreateTap,
    },
    {
      key: 'earn',
      icon: {
        defaultName: 'tabbar/pepper.svg',
        activeName: 'tabbar/pepper-filled.svg',
      },
      action: () => this.overlayService.openGetAppDialog(AnalyticsEventsEnum.EarnTap, 'lee'),
      eventName: AnalyticsEventsEnum.EarnTap,
    },
    {
      key: 'market',
      icon: {
        defaultName: 'tabbar/market.svg',
        activeName: 'tabbar/market.svg',
      },
      link: this.isMobile() ? '/market/offers' : undefined,
      action: () =>this.overlayService.openGetAppDialog(AnalyticsEventsEnum.MarketTap, 'lee'),
      eventName: AnalyticsEventsEnum.MarketTap,
    },
    {
      key: 'profile',
      icon: {
        defaultName: 'tabbar/profile.svg',
        activeName: 'tabbar/profile-filled.svg',
      },
      action: () => this.overlayService.openGetAppDialog(AnalyticsEventsEnum.MyProfileTap, 'lee'),
      eventName: AnalyticsEventsEnum.MyProfileTap,
    },
  ];

  ngAfterViewInit(): void {
    this.resizeHandler();
  }

  protected onItemClick(item: NavBarItem, event: Event): void {
    if (!item.link) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (item.action) {
      item.action();
    }
  }

  private resizeHandler(): void {
    const nativeEl = this.elRef.nativeElement as HTMLElement;

    this.navBarWidth.set(nativeEl.offsetWidth);
    this.navBarHeight.set(nativeEl.offsetHeight);
  }
}
